import Divider from '@/components/Divider';
import { PromptsAreaIcon } from '@/components/Icons';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import useGeneratorStore, { GeneratorTypeEnums } from '@/state/generator-store';
import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { quickPromptsList } from './enums';
import PastPromptSelect from './PastPromptSelect';
import CardContainer from '../ui/CardContainer';
import { useDimensions } from '@/hooks/useDimensions';

function calculateSpeed(text: string, baseSpeed = 75, minSpeed = 20, adjustmentFactor = 2) {
  const dynamicSpeed = Math.max(baseSpeed - text.length * adjustmentFactor, minSpeed);
  return dynamicSpeed;
}

const randomizeQuickPrompts = (num = 7) => {
  const shuffledPrompts = [...quickPromptsList].sort(() => Math.random() - 0.5);
  return shuffledPrompts.slice(0, num);
};

const PromptTextTextarea: React.FC = () => {
  const [quickPrompts, setQuickPrompts] = useState(randomizeQuickPrompts());
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const tagsContainerRef = React.useRef<HTMLDivElement>(null);
  const selectContainerRef = React.useRef<HTMLDivElement>(null);

  const { generatorType, control, formValues, generatorForm, audioInfo } = useGeneratorStore((state) => ({
    generatorType: state.generatorType,
    control: state.control,
    formValues: state.formValues,
    generatorForm: state.generatorForm,
    audioInfo: state.audioInfo,
  }));

  const {
    field: { value, onChange },
  } = useController({
    name: 'prompt',
    control,
    defaultValue:
      generatorType === GeneratorTypeEnums.EDITOR ? ((audioInfo as any)?.prompts ?? (audioInfo as any)?.prompt) : formValues?.prompt || '',
  });

  const { watch, setValue } = generatorForm ?? {};

  const content = watch?.('prompt') ?? '';

  const handleClickBadge = (p: { key?: string; value: any }) => {
    const newPrompt = value + `${value ? ', ' : ''}${p.value}`;
    onChange(newPrompt);
    setIsTyping(true);
    setValue?.('prompt', newPrompt);
    setDisplayText(value + (value ? ', ' : ''));
  };

  const handleClickPastPrompt = (prompt: string) => {
    onChange(prompt);
    setIsTyping(true);
    setValue?.('prompt', prompt);
    setDisplayText(value + (value ? ', ' : ''));
  };

  useEffect(() => {
    if (isTyping && displayText !== content) {
      const timeoutId = setTimeout(() => {
        setDisplayText(content.slice(0, displayText.length + 1));
      }, calculateSpeed(content));
      return () => clearTimeout(timeoutId);
    } else if (isTyping && displayText === content) {
      setIsTyping(false);
    }
  }, [isTyping, content, displayText]);

  useEffect(() => {
    setQuickPrompts(randomizeQuickPrompts());
  }, []);

  const { isLargePC, isMobileOrPad } = useDimensions();

  useEffect(() => {
    if (generatorType === GeneratorTypeEnums.EDITOR && ((audioInfo as any)?.prompts ?? (audioInfo as any)?.prompt)) {
      setValue?.('prompt', (audioInfo as any)?.prompts ?? (audioInfo as any)?.prompt);
    }
  }, [generatorType, audioInfo]);

  return (
    <div className="flex flex-col bg-[#191A1B] rounded-lg p-1 w-full h-full">
      <div
        style={{
          height: `calc(100% - ${tagsContainerRef?.current?.clientHeight ?? 0}px)`,
        }}
      >
        <CardContainer showBorder={false}>
          <div
            className="p-4 w-full"
            style={{
              background: 'linear-gradient(#191A1B, #1E2021)',
              boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
            }}
          >
            <div className="flex justify-between w-full" ref={headerRef}>
              <label className="text-xs text-gray-400 pointer-events-none">
                <div className="font-pressura-mono text-[10px] font-normal text-left text-jen-orange">TEXT INPUT</div>
              </label>
              <PromptsAreaIcon
                className="w-24 h-6 rounded-sm cursor-pointer hover:bg-jen-orange/20 transition-all duration-100"
                onClick={() => {
                  setQuickPrompts(randomizeQuickPrompts());
                }}
              />
            </div>
            <input disabled className="opacity-0 absolute -top-[99px]" />
            <Textarea
              className="flex-1 bg-transparent h-44 px-0 text-white text-lg rounded-lg border-none min-h-32 bg-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none resize-none touch-none placeholder:text-gray-500"
              value={isTyping ? displayText : value}
              placeholder={showPlaceholder ? `Imagine a song. What does it sound like?` : ''}
              autoCorrect="off"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={onChange}
              onFocus={() => setShowPlaceholder(false)}
              onBlur={() => setShowPlaceholder(true)}
            />
          </div>
          <div className="mx-2">
            <Divider dashed={true} />
          </div>
          <div ref={selectContainerRef}>
            <PastPromptSelect handleClickPastPrompt={handleClickPastPrompt} />
          </div>
        </CardContainer>
      </div>
      <div className="mt-1" ref={tagsContainerRef}>
        <CardContainer showBorder={false}>
          <div
            className="p-2 w-full"
            style={{
              background: 'linear-gradient(#191A1B, #1E2021)',
              boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
            }}
          >
            <div className={`flex flex-wrap ${isLargePC || isMobileOrPad ? 'max-h-16' : 'max-h-24'} overflow-y-hidden`}>
              <Badge
                variant="noBackground"
                size="mini"
                className="py-0.5 cursor-pointer rounded-md mr-2 my-1 font-pressura-mono font-medium uppercase text-jen-orange"
              >
                input inspiration:
              </Badge>
              {quickPrompts.map((p) => {
                return (
                  <Badge
                    key={p.key}
                    variant="info"
                    size="mini"
                    className="px-2 py-0.5 cursor-pointer rounded-sm mr-2 my-1 font-pressura-mono font-medium uppercase"
                    onClick={() => handleClickBadge(p)}
                  >
                    {p.key}
                  </Badge>
                );
              })}
            </div>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

export default PromptTextTextarea;
